import { useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import { useBrand } from "../context/BrandContext";
import CatalogExercisePicker, { catalogExerciseLabel } from "./CatalogExercisePicker";
import { changeExerciseOnResults, getexercise } from "./funtionsBD/Mainservice";
import { catalogFilterFromTrainingBlock } from "../utils/addExerciseNavigation";
import { needsCatalogExercise } from "../utils/resultsExerciseCatalog";
import { getPlayerDisplayName } from "../utils/sessionResultsPlayers";
import { localizedExerciseName, localizedField } from "../utils/localizedCatalogFields";
import { normalizeAppLang } from "../utils/appLanguages";

function playerRowId(player) {
  if (!player) return "";
  return String(player.player_id || player.id || "").trim();
}

/**
 * After a session: set or change the catalog exercise that was performed.
 * Type-only blocks load the full type catalog list; coaches can apply to
 * all players present or to one specific player.
 */
export default function ChangeExerciseOnResultsDialog({
  open,
  onClose,
  teId,
  currentExercise = null,
  currentName = "",
  defaultScope = "player",
  allowPlayerScope = true,
  playerId = null,
  playerName = "",
  players = [],
  groupId = null,
  onSuccess,
}) {
  const { t, i18n } = useTranslation();
  const { brand } = useBrand();
  const primary = brand?.color_primary || "#0877BC";
  const lang = normalizeAppLang(i18n.language);

  const settingPerformed = needsCatalogExercise(currentExercise);
  const catalogFilter = useMemo(
    () => catalogFilterFromTrainingBlock(currentExercise, lang),
    [currentExercise, lang]
  );
  const useTypeCatalog = Boolean(
    settingPerformed && (catalogFilter.typeId || catalogFilter.cbId)
  );

  const presentPlayers = useMemo(() => {
    const list = Array.isArray(players) ? players : [];
    return list.filter((p) => playerRowId(p));
  }, [players]);

  const fixedPlayerId = playerId ? String(playerId) : "";
  const canPickAmongPlayers =
    allowPlayerScope && !fixedPlayerId && presentPlayers.length > 0;

  const [scope, setScope] = useState(defaultScope);
  const [scopePlayerId, setScopePlayerId] = useState(fixedPlayerId);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [typeCatalog, setTypeCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    setScope(allowPlayerScope ? defaultScope : "all");
    setScopePlayerId(fixedPlayerId);
    setSelected(null);
    setBusy(false);
    setError(null);
    setQuery("");
    setCatalogError("");
  }, [open, defaultScope, allowPlayerScope, fixedPlayerId]);

  useEffect(() => {
    if (!open || !useTypeCatalog) {
      setTypeCatalog([]);
      setCatalogLoading(false);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setCatalogLoading(true);
      setCatalogError("");
      try {
        const params = {
          type_id: catalogFilter.typeId || undefined,
          sub_type_id: catalogFilter.subTypeId || undefined,
          cb_id: catalogFilter.cbId || undefined,
          group_id: groupId || undefined,
        };
        const response = await getexercise(params);
        if (cancelled) return;
        const rows = response?.data?.data || [];
        setTypeCatalog(Array.isArray(rows) ? rows : []);
        if (!rows?.length) {
          setCatalogError(
            t("noCatalogExercisesFound") ||
              "No catalog exercises found for this type."
          );
        }
      } catch (err) {
        if (!cancelled) {
          setTypeCatalog([]);
          setCatalogError(
            err?.message ||
              t("errorLoadingExercises") ||
              "Could not load catalog exercises."
          );
        }
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    useTypeCatalog,
    catalogFilter.typeId,
    catalogFilter.subTypeId,
    catalogFilter.cbId,
    groupId,
    t,
  ]);

  const filteredTypeCatalog = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return typeCatalog;
    return typeCatalog.filter((ex) => {
      const name = localizedExerciseName(ex, lang).toLowerCase();
      const objective = localizedField(ex, "ex_objective", lang).toLowerCase();
      return name.includes(q) || objective.includes(q);
    });
  }, [typeCatalog, query, lang]);

  const currentLabel = useMemo(() => {
    if (currentName) return currentName;
    if (catalogFilter.label) return catalogFilter.label;
    return catalogExerciseLabel(currentExercise, lang);
  }, [currentName, currentExercise, catalogFilter.label, lang]);

  const effectivePlayerId =
    scope === "player" ? String(scopePlayerId || fixedPlayerId || "").trim() : "";

  const canSubmit = Boolean(
    teId &&
      selected?.ex_id &&
      !busy &&
      (scope === "all" || effectivePlayerId)
  );

  const handleApply = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const response = await changeExerciseOnResults({
        te_id: teId,
        replace_ex_id: selected.ex_id,
        scope,
        player_id: scope === "player" ? effectivePlayerId : undefined,
      });
      const data = response?.data?.data || response?.data || {};
      onSuccess?.(data);
      onClose?.();
    } catch (err) {
      setError(
        err?.message ||
          t("changeExerciseError", "Could not change the exercise")
      );
    } finally {
      setBusy(false);
    }
  };

  const title = settingPerformed
    ? t("setExercisePerformed", "Set exercise performed")
    : t("changeExerciseOnResults", "Change exercise");
  const confirmLabel = settingPerformed
    ? t("setExercisePerformedConfirm", "Set exercise")
    : t("changeExerciseConfirm", "Change and re-evaluate");
  const pickLabel = settingPerformed
    ? t("setExercisePerformedPick", "Exercise performed")
    : t("changeExercisePickReplacement", "Replacement exercise");
  const hint = useTypeCatalog
    ? t(
        "setExercisePerformedTypeHint",
        "Choose from exercises of this type. Apply to everyone present or to one player."
      )
    : t(
        "changeExerciseOnResultsHint",
        "Videos will be re-evaluated with the new exercise recipe."
      );

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: 16000 }}
      PaperProps={{ sx: { borderRadius: "16px", maxHeight: "90vh" } }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <SwapHorizIcon sx={{ color: primary }} />
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem" }}>
              {title}
            </Typography>
            {currentLabel ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {currentLabel}
              </Typography>
            ) : null}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {allowPlayerScope ? (
          <FormControl component="fieldset" sx={{ mb: 2.5, width: "100%" }}>
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 0.75 }}>
              {t("changeExerciseScope", "Apply to")}
            </FormLabel>
            <RadioGroup
              value={scope}
              onChange={(e) => setScope(e.target.value)}
            >
              <FormControlLabel
                value="all"
                control={<Radio />}
                label={t(
                  "changeExerciseScopeAllPresent",
                  "All players present"
                )}
              />
              <FormControlLabel
                value="player"
                disabled={!fixedPlayerId && !canPickAmongPlayers}
                control={<Radio />}
                label={
                  fixedPlayerId
                    ? playerName
                      ? t(
                          "changeExerciseScopePlayerNamed",
                          "This player ({{name}})",
                          { name: playerName }
                        )
                      : t("changeExerciseScopePlayer", "This player")
                    : t("changeExerciseScopeSpecificPlayer", "A specific player")
                }
              />
            </RadioGroup>
            {scope === "player" && canPickAmongPlayers ? (
              <TextField
                select
                fullWidth
                size="small"
                sx={{ mt: 1.25 }}
                label={t("selectPlayer", "Select player")}
                value={scopePlayerId}
                onChange={(e) => setScopePlayerId(e.target.value)}
                disabled={busy}
              >
                {presentPlayers.map((p) => {
                  const id = playerRowId(p);
                  return (
                    <MenuItem key={id} value={id}>
                      {getPlayerDisplayName(p) || id}
                    </MenuItem>
                  );
                })}
              </TextField>
            ) : null}
          </FormControl>
        ) : null}

        {useTypeCatalog ? (
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
              {pickLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {hint}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                t("searchExercisesByNameOrDescription") ||
                "Search by name or description…"
              }
              disabled={busy || catalogLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1.5 }}
            />
            {catalogLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : catalogError && filteredTypeCatalog.length === 0 ? (
              <Alert severity="warning">{catalogError}</Alert>
            ) : filteredTypeCatalog.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                {t("noCatalogExercisesFound") || "No matching exercises."}
              </Typography>
            ) : (
              <List
                dense
                disablePadding
                sx={{
                  maxHeight: 320,
                  overflow: "auto",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                {filteredTypeCatalog.map((ex) => {
                  const selectedHere =
                    String(selected?.ex_id || "") === String(ex.ex_id);
                  const name = localizedExerciseName(ex, lang) || ex.ex_id;
                  const objective = localizedField(ex, "ex_objective", lang);
                  return (
                    <ListItemButton
                      key={ex.ex_id}
                      selected={selectedHere}
                      disabled={busy}
                      onClick={() => setSelected(ex)}
                      sx={{
                        alignItems: "flex-start",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                        "&.Mui-selected": {
                          backgroundColor: `${primary}18`,
                        },
                        "&.Mui-selected:hover": {
                          backgroundColor: `${primary}28`,
                        },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {name}
                          </Typography>
                        }
                        secondary={
                          objective ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              {objective}
                            </Typography>
                          ) : null
                        }
                      />
                    </ListItemButton>
                  );
                })}
              </List>
            )}
          </Box>
        ) : (
          <CatalogExercisePicker
            value={selected}
            onChange={setSelected}
            disabled={busy}
            label={pickLabel}
            helperText={hint}
          />
        )}

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>
          {t("cancel", "Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!canSubmit}
          sx={{ backgroundColor: primary, "&:hover": { backgroundColor: primary } }}
        >
          {busy ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            confirmLabel
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
